import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown, Lock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { VimeoPlayer } from "../components/VimeoPlayer";
import { Reveal, KineticLines } from "../components/Reveal";
import { track } from "../lib/tracking";

export default function Home() {
  const navigate = useNavigate();
  const [ctaUnlocked, setCtaUnlocked] = useState(() => !!localStorage.getItem("pv_ms_1217572139_90"));
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const percentY = useTransform(scrollYProgress, [0, 1], [0, 180]);

  const goToServizio = () => {
    track("CTA1Click", { cta: "Vai al Video di Spiegazione del Servizio" });
    navigate("/servizio");
  };

  return (
    <div className="bg-[#F7F2EA] min-h-screen" data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative bg-[#2B1930] pv-grain overflow-hidden">
        <Navbar dark />
        <motion.div
          style={{ y: percentY }}
          className="pv-giant-percent absolute -right-10 top-24 text-[38vw] md:text-[26rem] text-[#D83A52]/10 z-0"
          aria-hidden="true"
        >
          %
        </motion.div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-36 pb-10 md:pt-44">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.35em] font-bold text-[#C8A6A1] mb-8"
          >
            Agenzia di Marketing · Solo Saloni da Parrucchiere
          </motion.p>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-[#F7F2EA] max-w-4xl"
            data-testid="home-headline"
          >
            <KineticLines
              lines={[
                <span key="1">Come riempire di</span>,
                <span key="2" className="italic text-[#D83A52]">clienti altospendenti</span>,
                <span key="3">il tuo Salone da Parrucchiere</span>,
                <span key="4">
                  tutto l'anno<span className="text-[#D83A52]">.</span>
                </span>,
              ]}
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex items-center gap-3 text-[#F7F2EA]/60 text-sm"
          >
            <ArrowDown className="h-4 w-4 text-[#D83A52] animate-bounce" />
            Guarda il video qui sotto per scoprire come
          </motion.div>
        </div>

        {/* VIDEO */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <VimeoPlayer
              videoId="1217572139"
              hash="b3674bc1c5"
              label="VSL1"
              testId="home-vsl-video"
              onMilestone={(m) => m >= 90 && setCtaUnlocked(true)}
            />
          </motion.div>
        </div>

        {/* CTA - compare solo al 90% del video */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-24 text-center">
          <AnimatePresence mode="wait">
            {ctaUnlocked ? (
              <motion.button
                key="cta"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                onClick={goToServizio}
                data-testid="home-cta-button"
                className="pv-cta-shine inline-flex items-center gap-3 rounded-full bg-[#D83A52] text-white font-bold text-base sm:text-lg px-10 py-5 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-transform shadow-[0_8px_25px_rgba(216,58,82,0.45)]"
              >
                Vai al Video di Spiegazione del Servizio
                <ArrowRight className="h-5 w-5" />
              </motion.button>
            ) : (
              <motion.p
                key="hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 1.1, duration: 0.8 }}
                data-testid="home-cta-locked-hint"
                className="inline-flex items-center gap-2 text-sm text-[#F7F2EA]/50"
              >
                <Lock className="h-4 w-4 text-[#C8A6A1]" />
                Guarda il video: il pulsante per proseguire comparirà qui
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </section>

      <Marquee />

      {/* FINAL CTA BAND - compare solo al 90% del video */}
      {ctaUnlocked && (
        <section className="bg-[#2B1930] pv-grain relative overflow-hidden">
          <div className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#F7F2EA] leading-[1.15]">
                Vuoi sapere <span className="italic text-[#D83A52]">esattamente</span> come funziona?
              </h2>
              <p className="mt-4 text-[#F7F2EA]/60 max-w-xl mx-auto text-base">
                Nel secondo video ti spiego per filo e per segno il servizio: le consulenze, la messa
                a terra e il pagamento a % solo sull'aumento di fatturato.
              </p>
              <button
                onClick={goToServizio}
                data-testid="home-cta-bottom-button"
                className="pv-cta-shine mt-10 inline-flex items-center gap-3 rounded-full bg-[#D83A52] text-white font-bold text-base px-10 py-5 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-transform shadow-[0_8px_25px_rgba(216,58,82,0.45)]"
              >
                Vai al Video di Spiegazione del Servizio
                <ArrowRight className="h-5 w-5" />
              </button>
            </Reveal>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

